import React from 'react';
import { CircuitRenderer, CircuitElement } from './CircuitRenderer';

interface InductorCombinationDiagramProps {
    inductors: number[];
    equivalent: number;
    isSeries: boolean;
}

export const InductorCombinationDiagram: React.FC<InductorCombinationDiagramProps> = ({
    inductors,
    equivalent,
    isSeries
}) => {
    const width = 450;
    const height = 300;
    const padding = 50;

    const elements: CircuitElement[] = [];

    if (isSeries) {
        // Series layout: horizontal line with inductors in sequence
        const topY = height / 2;
        const startX = padding;
        const endX = width - padding;
        const totalWidth = endX - startX;

        // Divide space evenly among inductors
        const segmentWidth = totalWidth / inductors.length;

        inductors.forEach((ind, i) => {
            const segmentStart = startX + i * segmentWidth;
            const segmentEnd = segmentStart + segmentWidth;

            // Wire before inductor (except for first)
            if (i === 0) {
                elements.push({
                    type: 'wire',
                    from: { x: segmentStart, y: topY },
                    to: { x: segmentStart + segmentWidth * 0.2, y: topY }
                });
            }

            // Inductor
            elements.push({
                type: 'inductor',
                from: { x: segmentStart + segmentWidth * 0.2, y: topY },
                to: { x: segmentEnd - segmentWidth * 0.2, y: topY },
                value: ind,
                label: `L${i + 1}=${ind}mH`,
                labelSide: 'left'
            });

            // Wire after inductor
            if (i < inductors.length - 1) {
                elements.push({
                    type: 'wire',
                    from: { x: segmentEnd - segmentWidth * 0.2, y: topY },
                    to: { x: segmentEnd + segmentWidth * 0.2, y: topY }
                });
            } else {
                elements.push({
                    type: 'wire',
                    from: { x: segmentEnd - segmentWidth * 0.2, y: topY },
                    to: { x: segmentEnd, y: topY }
                });
            }
        });
    } else {
        // Parallel layout: vertical inductors between two rails
        const topY = padding + 30;
        const bottomY = height - padding - 30;
        const startX = padding + 20;
        const endX = width - padding - 20;
        const totalWidth = endX - startX;

        // Top and bottom rails
        elements.push({
            type: 'wire',
            from: { x: startX, y: topY },
            to: { x: endX, y: topY }
        });
        elements.push({
            type: 'wire',
            from: { x: startX, y: bottomY },
            to: { x: endX, y: bottomY }
        });

        // Vertical inductors
        const spacing = totalWidth / (inductors.length + 1);

        inductors.forEach((ind, i) => {
            const x = startX + spacing * (i + 1);

            elements.push({
                type: 'inductor',
                from: { x, y: topY },
                to: { x, y: bottomY },
                value: ind,
                label: `L${i + 1}=${ind}mH`,
                labelSide: i % 2 === 0 ? 'left' : 'right' // Alternate sides to prevent overlap
            });
        });
    }

    // Add equivalent inductance as a junction label
    const junctions = [
        {
            x: width - padding - 30,
            y: isSeries ? height / 2 + 40 : height - 30,
            label: `Leq = ${equivalent}mH`,
            labelPosition: 'bottom' as const,
            color: 'transparent'
        }
    ];

    return (
        <CircuitRenderer
            width={width}
            height={height}
            elements={elements}
            junctions={junctions}
        />
    );
};
